"use client";

import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { useCornice } from "@/app/components/Chrome";
import { FilterChips, rimuoviDaIntent, type Chip } from "@/app/components/FilterChips";
import { IconaGraffetta, IconaInvia, IconaYouppy } from "@/app/components/Icone";
import { Logo } from "@/app/components/Logo";
import { Carosello } from "@/app/components/Carosello";
import { ResultCard } from "@/app/components/ResultCard";
import { Fiducia } from "@/app/components/Fiducia";
import { Scaffale } from "@/app/components/Scaffale";
import {
  catalogSection,
  executeSearch,
  localized,
  nomePaese,
  searchImage,
  searchText,
  type ImageHit,
  type ProductHit,
  type SearchIntent,
  type ShelfSection,
} from "@/lib/api";

/**
 * La ricerca assistita: una conversazione che produce risultati.
 *
 * Tre impaginazioni, e non sono tre pagine: è la stessa schermata che si
 * riorganizza secondo due sole condizioni — se c'è il menù a tendina, e se ci
 * sono risultati.
 *
 *   nessun risultato   marchio grande al centro, chat sotto
 *   menù aperto        striscia di schede sopra la chat, che scorre in orizzontale
 *   menù chiuso        griglia da tre a sinistra che scorre in verticale, chat in colonna a destra
 *
 * La ragione della differenza è lo spazio: con la tendina aperta la larghezza
 * disponibile non basta per tre colonne di schede più una chat accanto, quindi
 * le schede diventano una fila che scorre. Non è una preferenza grafica, è
 * quello che ci sta.
 *
 * Nota su cosa si mostra: i risultati sono **prodotti e lavorazioni**, non
 * fornitori. Chi produce non compare mai — è la regola su cui è costruito il
 * modello di rivendita, non una scelta di questa schermata.
 */

/**
 * Se una media query e' soddisfatta adesso.
 *
 * La larghezza della finestra e' uno stato che vive **fuori da React**: si legge
 * con lo strumento fatto per questo, non con uno stato piu' un effetto che lo
 * insegue. Sul server non c'e' finestra, e la risposta e' «no»: la disposizione
 * impilata e' quella che funziona ovunque, quindi e' il ripiego giusto.
 */
function useMedia(query: string): boolean {
  const sottoscrivi = useMemo(
    () => (avvisa: () => void) => {
      const mq = window.matchMedia(query);
      mq.addEventListener("change", avvisa);
      return () => mq.removeEventListener("change", avvisa);
    },
    [query],
  );
  return useSyncExternalStore(
    sottoscrivi,
    () => window.matchMedia(query).matches,
    () => false,
  );
}

type Messaggio =
  | { chi: "persona"; testo: string }
  | { chi: "agente"; testo: string; intent?: SearchIntent };

const ESEMPI = [
  "componenti in alluminio per automotive in Germania, piccoli lotti",
  "chi lavora la fibra di carbonio per prototipi?",
  "tornitura CNC di precisione in acciaio inox in Italia",
];

/** Che cosa ha capito il sistema, detto a parole. */
function riassunto(intent: SearchIntent, quanti: number, rilassati: string[]): string {
  const parti: string[] = [];
  if (intent.semantic_query) parti.push(`«${intent.semantic_query}»`);
  if (intent.countries.length)
    parti.push(`in ${intent.countries.map(nomePaese).join(", ")}`);
  if (intent.materials.length) parti.push(`in ${intent.materials.join(", ")}`);
  if (intent.processes.length) parti.push(`con ${intent.processes.join(", ")}`);
  if (intent.max_order_quantity)
    parti.push(`fino a ${intent.max_order_quantity} pezzi di lotto minimo`);
  if (intent.max_lead_time_days) parti.push(`entro ${intent.max_lead_time_days} giorni`);

  const cercato = parti.length ? `Ho cercato ${parti.join(", ")}.` : "Ho cercato.";
  const trovato =
    quanti === 0
      ? " Non ho trovato niente: prova a togliere un vincolo o a descrivere il pezzo in altro modo."
      : ` Ho trovato ${quanti} ${quanti === 1 ? "prodotto" : "prodotti"}.`;
  const allentato = rilassati.length
    ? ` Con tutti i filtri non usciva nulla, così ho allentato ${rilassati.join(", ")}.`
    : "";

  return cercato + trovato + allentato;
}

/* ── La chat ────────────────────────────────────────────── */

/** La firma dell'agente accanto a quello che dice, e che gira mentre pensa. */
function Firma({ pensa }: { pensa?: boolean }) {
  return (
    <div
      aria-hidden
      className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-accent-soft text-accent"
    >
      <IconaYouppy className={`size-[18px] ${pensa ? "youppy-gira" : ""}`} />
    </div>
  );
}

function ChatIA({
  messaggi,
  intent,
  inCorso,
  errore,
  onInvia,
  onRimuoviChip,
  onImmagine,
  compatta,
  piena,
}: {
  messaggi: Messaggio[];
  intent: SearchIntent | null;
  inCorso: boolean;
  errore: string | null;
  onInvia: (testo: string) => void;
  onRimuoviChip: (chip: Chip) => void;
  onImmagine: (file: File) => void;
  /** In colonna stretta il riquadro cresce, in orizzontale resta basso. */
  compatta?: boolean;
  /** Riempie il contenitore invece di crescere con il contenuto. */
  piena?: boolean;
}) {
  const [testo, setTesto] = useState("");
  const fine = useRef<HTMLDivElement>(null);
  const file = useRef<HTMLInputElement>(null);
  const campo = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    fine.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messaggi.length, inCorso]);

  /* Il campo cresce con il testo fino a un tetto, poi scorre. Si azzera prima di
     misurare, altrimenti l'altezza precedente fa da pavimento e il campo non
     torna più piccolo quando si cancella. */
  function adatta(el: HTMLTextAreaElement) {
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }

  const puoInviare = testo.trim().length >= 2 && !inCorso;

  function invia() {
    if (!puoInviare) return;
    onInvia(testo.trim());
    setTesto("");
    if (campo.current) {
      campo.current.style.height = "auto";
      campo.current.focus();
    }
  }

  return (
    <section
      className={`flex min-h-0 flex-col overflow-hidden rounded-2xl border border-line bg-surface shadow-sm ${
        piena ? "h-full" : ""
      }`}
      aria-label="Ricerca assistita"
    >
      {/* Le risposte dell'agente e quello che si è chiesto. */}
      <div
        className={`min-h-0 flex-1 space-y-4 overflow-y-auto p-4 ${
          compatta ? "max-h-[38vh]" : ""
        }`}
      >
        {/* Gli esempi sono il primo messaggio dell'agente, non un elenco accanto
            alla chat: chi non sa da dove cominciare li trova dove guarderebbe
            comunque, e spariscono da soli appena si scrive qualcosa — perché la
            loro condizione è "nessun messaggio", non un interruttore da
            ricordarsi di spegnere. */}
        {messaggi.length === 0 && (
          <div className="flex items-start gap-2.5">
            <Firma />
            <div className="max-w-[85%] sm:max-w-prose rounded-2xl rounded-tl-md bg-surface-strong px-3.5 py-2.5 text-sm leading-relaxed text-on-strong">
              {/* AI Act art. 50: chi parla con un sistema di IA deve saperlo, e
                  deve saperlo *prima*, non a metà conversazione. Sta qui perché
                  questo riquadro esiste esattamente finché non si è ancora
                  scritto niente — l'unico momento in cui l'avviso arriva in
                  tempo. Serve tanto più adesso che la riga sotto invita a
                  scrivere come si scriverebbe a una persona. */}
              <p className="text-xs font-medium uppercase tracking-wide text-on-strong-muted">
                Assistente automatico
              </p>
              <p className="mt-1.5">
                Scrivi come parleresti a un collega. Ai filtri ci penso io, e ti mostro
                quali ho applicato.
              </p>
              <ul className="mt-3 space-y-1.5">
                {ESEMPI.map((esempio) => (
                  <li key={esempio}>
                    <button
                      type="button"
                      onClick={() => onInvia(esempio)}
                      className="w-full rounded-xl bg-on-strong/20 px-3 py-2 text-left text-[13px] text-on-strong transition hover:bg-on-strong/30"
                    >
                      {esempio}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {messaggi.map((m, i) =>
          m.chi === "persona" ? (
            <div key={i} className="flex justify-end">
              <p className="max-w-[85%] sm:max-w-prose rounded-2xl rounded-br-md bg-accent px-3.5 py-2.5 text-sm leading-relaxed text-accent-contrast">
                {m.testo}
              </p>
            </div>
          ) : (
            <div key={i} className="flex items-start gap-2.5">
              <Firma />
              <p className="max-w-[85%] sm:max-w-prose rounded-2xl rounded-tl-md bg-surface-strong px-3.5 py-2.5 text-sm leading-relaxed text-on-strong">
                {m.testo}
              </p>
            </div>
          ),
        )}

        {/* L'attesa è tre puntini invece di una frase: dice la stessa cosa
            occupando la riga che il messaggio prenderà comunque. */}
        {inCorso && (
          <div className="flex items-start gap-2.5">
            <Firma pensa />
            <p
              role="status"
              aria-label="Sto cercando"
              className="puntini flex items-center gap-1 rounded-2xl rounded-tl-md bg-surface-strong px-3.5 py-3"
            >
              <span className="size-1.5 rounded-full bg-on-strong" />
              <span className="size-1.5 rounded-full bg-on-strong" />
              <span className="size-1.5 rounded-full bg-on-strong" />
            </p>
          </div>
        )}

        {errore && (
          // Una nuvoletta come quelle dell'assistente: il rosso puro sul fondo
          // blu della chat non si leggeva (contrasto sotto 3:1 sul tema scuro).
          <p
            role="alert"
            className="max-w-[85%] rounded-2xl rounded-tl-md border-l-4 border-red-500 bg-surface-strong px-3.5 py-2.5 text-sm text-on-strong"
          >
            {errore}
          </p>
        )}

        <div ref={fine} />
      </div>

      {/* I filtri estratti, rimovibili: è il modo in cui l'agente mostra il
          proprio ragionamento invece di limitarsi a dichiararlo. */}
      {intent && (
        <div className="px-4 pb-1">
          <FilterChips intent={intent} onRimuovi={onRimuoviChip} />
        </div>
      )}

      {/* Dove si scrive. Il campo è il riquadro: i pulsanti gli stanno dentro,
          invece di stargli accanto come in una barra di ricerca. */}
      <div className="p-3">
        <div className="rounded-2xl border border-line bg-surface-strong p-2 transition focus-within:border-accent">
          <textarea
            ref={campo}
            rows={1}
            value={testo}
            onChange={(e) => {
              setTesto(e.target.value);
              adatta(e.currentTarget);
            }}
            onKeyDown={(e) => {
              // Invio manda, maiuscolo+invio va a capo: è quello che le dita si
              // aspettano da una chat, e il campo può comunque contenere più
              // righe per una specifica lunga.
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                invia();
              }
            }}
            placeholder="Descrivi quello che ti serve…"
            aria-label="Descrivi quello che ti serve"
            className="block max-h-40 w-full resize-none bg-transparent px-2 py-1.5 text-sm leading-relaxed text-on-strong outline-none placeholder:text-on-strong-muted"
          />

          <div className="mt-1 flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => file.current?.click()}
              aria-label="Carica una foto del pezzo"
              title="Carica una foto del pezzo"
              className="flex size-9 items-center justify-center rounded-full text-on-strong-muted transition hover:bg-on-strong/15 hover:text-on-strong"
            >
              <IconaGraffetta className="size-[18px]" />
            </button>

            <button
              type="button"
              onClick={invia}
              disabled={!puoInviare}
              aria-label="Cerca"
              className="flex size-9 items-center justify-center rounded-full bg-accent text-accent-contrast transition hover:opacity-90 disabled:opacity-30"
            >
              <IconaInvia className="size-[18px]" />
            </button>
          </div>
        </div>

        <input
          ref={file}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onImmagine(f);
            e.target.value = "";
          }}
        />
      </div>
    </section>
  );
}

/* ── I risultati, nelle due disposizioni ──────────────────────────────────── */

function Striscia({ risultati }: { risultati: ProductHit[] }) {
  // La stessa fila dello scaffale in home: stesse frecce, stessa barra. Erano
  // due copie, ed erano gia' divergenti.
  return (
    <Carosello etichetta="i risultati" passo={320}>
      {risultati.map((hit) => (
        <li key={hit.product_id} className="w-[250px] shrink-0">
          <ResultCard hit={hit} price={hit.price} />
        </li>
      ))}
    </Carosello>
  );
}

/** Tre per riga dove ci stanno: e' il ritmo del disegno, e con l'immagine sopra
 *  tre schede stanno comode anche accanto alla colonna della chat. */
function Griglia({ risultati }: { risultati: ProductHit[] }) {
  return (
    <ul className="grid grid-cols-2 gap-4 xl:grid-cols-3">
      {risultati.map((hit) => (
        <li key={hit.product_id}>
          <ResultCard hit={hit} price={hit.price} />
        </li>
      ))}
    </ul>
  );
}

/* ── L'orchestratore ──────────────────────────────────────────────────────── */

export function Ricerca() {
  const { menuAperto, setLogoNellaPagina } = useCornice();

  /* C'è posto per due colonne? La soglia sale quando il menù è aperto, perché
     quella colonna se ne va. Sta qui in cima e non accanto al punto in cui
     serve: i ganci si chiamano sempre, nello stesso ordine, e più in basso
     c'è già un ritorno anticipato. */
  const dueColonne = useMedia(
    menuAperto ? "(min-width: 900px)" : "(min-width: 640px)",
  );

  const [messaggi, setMessaggi] = useState<Messaggio[]>([]);
  const [risultati, setRisultati] = useState<ProductHit[] | null>(null);
  const [intent, setIntent] = useState<SearchIntent | null>(null);
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  const vuoto = risultati === null;

  // Nello stato vuoto il marchio sta grande in mezzo alla pagina: la barra si
  // toglie il proprio, che sarebbe lo stesso segno due volte.
  useEffect(() => {
    setLogoNellaPagina(vuoto);
    return () => setLogoNellaPagina(false);
  }, [vuoto, setLogoNellaPagina]);

  async function chiedi(testo: string) {
    setMessaggi((m) => [...m, { chi: "persona", testo }]);
    setInCorso(true);
    setErrore(null);
    try {
      const esito = await searchText(testo, 24);
      setIntent(esito.intent);
      setRisultati(esito.results);
      setMessaggi((m) => [
        ...m,
        {
          chi: "agente",
          testo: riassunto(esito.intent, esito.results.length, esito.relaxed_filters),
          intent: esito.intent,
        },
      ]);
    } catch (e) {
      setErrore(e instanceof Error ? e.message : "Ricerca non riuscita.");
    } finally {
      setInCorso(false);
    }
  }

  /**
   * Aprire un reparto dello scaffale.
   *
   * Due strade, e la differenza non e' un dettaglio di implementazione.
   *
   * Un reparto **di settore** e' un filtro vero: passa dalla ricerca come se
   * l'utente l'avesse scritto, e da li' in poi si comporta come una frase —
   * chip rimovibili, «e in Germania?» che funziona, la conversazione che
   * continua. Se lo caricassimo come elenco chiuso, il primo tentativo di
   * affinarlo ripartirebbe da zero.
   *
   * Gli altri reparti nascono da un fatto che una frase non sa dire — quanto si
   * e' venduto, quando e' entrato a catalogo, se e' della linea nostra — e
   * arrivano come elenco gia' fatto, senza chip da togliere.
   */
  async function apriSezione(sezione: ShelfSection) {
    setInCorso(true);
    setErrore(null);
    const nome = localized(sezione.title);
    try {
      if (sezione.kind === "settore") {
        const intento: SearchIntent = {
          semantic_query: "",
          countries: [],
          nace_prefixes: [],
          materials: [],
          processes: [],
          sectors: sezione.sectors,
          certifications: [],
          max_order_quantity: null,
          max_lead_time_days: null,
          language: "it",
        };
        const esito = await executeSearch(intento, 24);
        setIntent(esito.intent);
        setRisultati(esito.results);
        setMessaggi((m) => [
          ...m,
          {
            chi: "agente",
            testo:
              `Reparto ${nome}. ` +
              riassunto(esito.intent, esito.results.length, esito.relaxed_filters) +
              " Togli un filtro qui sotto o scrivimi come restringere.",
            intent: esito.intent,
          },
        ]);
      } else {
        const esito = await catalogSection(sezione.slug, 24);
        setIntent(null);
        setRisultati(esito.results);
        setMessaggi((m) => [
          ...m,
          {
            chi: "agente",
            testo:
              `${nome}: ${esito.results.length} ${
                esito.results.length === 1 ? "prodotto" : "prodotti"
              }. Non e' una ricerca, e' una selezione — descrivimi quello che ti serve e la restringo.`,
          },
        ]);
      }
    } catch (e) {
      setErrore(e instanceof Error ? e.message : "Reparto non caricato.");
    } finally {
      setInCorso(false);
    }
  }

  async function togliChip(chip: Chip) {
    if (!intent) return;
    // Si riesegue sull'intent modificato invece di reinterpretare la frase:
    // ripartire dal testo rimetterebbe dentro il filtro appena tolto.
    const nuovo = rimuoviDaIntent(intent, chip);
    setInCorso(true);
    try {
      const esito = await executeSearch(nuovo, 24);
      setIntent(esito.intent);
      setRisultati(esito.results);
      setMessaggi((m) => [
        ...m,
        {
          chi: "agente",
          testo: riassunto(esito.intent, esito.results.length, esito.relaxed_filters),
        },
      ]);
    } catch (e) {
      setErrore(e instanceof Error ? e.message : "Ricerca non riuscita.");
    } finally {
      setInCorso(false);
    }
  }

  async function perImmagine(file: File) {
    setMessaggi((m) => [...m, { chi: "persona", testo: "📷 una foto del pezzo" }]);
    setInCorso(true);
    setErrore(null);
    try {
      const { results } = await searchImage(file);
      const come: ProductHit[] = (results as ImageHit[]).map((h) => ({
        ...h,
        score: h.vector_similarity,
        text_rank: null,
      }));
      setIntent(null);
      setRisultati(come);
      setMessaggi((m) => [
        ...m,
        {
          chi: "agente",
          testo:
            come.length === 0
              ? "Nessun prodotto visivamente simile. Il catalogo ha ancora poche immagini: la ricerca per foto migliora man mano che se ne aggiungono."
              : `Ho trovato ${come.length} ${come.length === 1 ? "prodotto simile" : "prodotti simili"} a quella foto.`,
        },
      ]);
    } catch (e) {
      setErrore(e instanceof Error ? e.message : "Ricerca per immagine non riuscita.");
    } finally {
      setInCorso(false);
    }
  }

  const chat = (compatta?: boolean, piena?: boolean) => (
    <ChatIA
      messaggi={messaggi}
      intent={intent}
      inCorso={inCorso}
      errore={errore}
      onInvia={chiedi}
      onRimuoviChip={togliChip}
      onImmagine={perImmagine}
      compatta={compatta}
      piena={piena}
    />
  );

  /* Stato vuoto: il marchio grande, la chat sotto. */
  if (vuoto) {
    return (
      <div className="mx-auto max-w-5xl px-6 py-10 sm:py-14">
        <div className="mx-auto flex max-w-2xl flex-col items-center">
          <Logo animato className="h-28 w-auto text-foreground sm:h-36" />

          <h1 className="mt-8 text-center text-xl font-semibold tracking-tight">
            Descrivi quello che ti serve.
          </h1>

          <div className="mt-6 w-full">{chat()}</div>
        </div>

        {/* Lo scaffale sta sotto la chat e non sopra: chi sa cosa cerca scrive e
            basta, chi non lo sa scorre. L'ordine dice quale dei due percorsi
            consideriamo principale. */}
        <Scaffale onApri={apriSezione} className="mt-12" />

        {/* Sotto il catalogo, al posto delle testimonianze: chi compra pezzi
            su specifica non si convince con «ottimo servizio», si convince
            sapendo chi risponde se il lotto arriva sbagliato. */}
        <Fiducia className="mt-14" />
      </div>
    );
  }

  /* Le due disposizioni dei risultati non dipendono dal menù, dipendono dallo
     **spazio**. Il menù aperto è solo uno dei modi di non averne: si toglie una
     colonna da 256 pixel al contenuto, e quello che restava per due colonne non
     basta più. Ma una finestra stretta con il menù chiuso è lo stesso problema,
     e prima veniva trattata in modo diverso — la chat scivolava sotto i
     prodotti restando larga quanto la pagina, invece di diventare la fila
     orizzontale che quella larghezza chiede.

     Quindi una condizione sola: c'è posto per due colonne? La soglia sale
     quando il menù è aperto, perché quella colonna se ne va. */
  /* Senza spazio: le schede diventano una fila che scorre e la chat va sotto,
     larga quanto la pagina. Una griglia da due colonne in trecento pixel non è
     una griglia, e una chat in colonna stretta non è una conversazione. */
  if (!dueColonne) {
    return (
      <div className="flex flex-col gap-4 px-4 py-6 sm:px-6">
        <Striscia risultati={risultati} />
        {chat(true)}
      </div>
    );
  }

  /* Con lo spazio: le schede a sinistra, che scorrono con la pagina, e la chat
     in una colonna a destra ferma e alta quanto lo schermo.

     `dvh` e non `vh`: sul telefono le barre del browser entrano ed escono, e
     `vh` misura lo schermo come se non ci fossero — il campo in cui si scrive
     finirebbe sotto la barra degli indirizzi. */
  return (
    <div className="grid grid-cols-[1fr_260px] gap-6 px-4 py-6 sm:px-6 md:grid-cols-[1fr_320px] xl:grid-cols-[1fr_400px]">
      <Griglia risultati={risultati} />
      <div className="sticky top-3 h-[calc(100dvh-1.5rem)]">
        {chat(false, true)}
      </div>
    </div>
  );
}
